import { DriveAccount } from 'jamespot-user-api';
import React, { useCallback, useState } from 'react';
import { JRCTypography } from '../../JRCTypography/JRCTypography';
import { JRCDisplayCard } from '../../JRCDisplayCard/JRCDisplayCard';
import styled, { useTheme } from 'styled-components';
import { JRCButtonDropdown } from '../../JRCButtonDropdown/JRCButtonDropdown';
import { useIntl } from 'react-intl';
import { JRCLoader } from '../../JRCLoader/JRCLoader';
import { JRCButton } from '../../JRCButton/JRCButton';
import { JDriveApi } from 'jamespot-user-api/lib/src/apis/drive/drive';
import { DriveService } from '../drive.types';
import { useAbortController } from '../../../hooks/useAbortController';

export type DriveAccountHeaderProps = {
    accounts?: DriveAccount[];
    activeAccount?: DriveAccount;
    isLogged: boolean;
    onLogout: () => void;
    onLogin: (account: DriveAccount) => void;
    onSetDefaultAccount: (account: DriveAccount) => void;
    handlers: {
        drive: Pick<JDriveApi, 'getAccount' | 'logout' | 'setDefaultAccount'>;
    };
    onLoginError: (errorMsg?: string) => void;
    service: DriveService;
    description?: string;
    onError?: (code: string) => void;
};

const Container = styled.div`
    margin-bottom: ${({ theme }) => theme.space.sm}px;
`;

const InnerContainer = styled.div`
    padding: ${({ theme }) => theme.space.sm}px ${({ theme }) => theme.space.md}px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Account = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.space.xs}px;
    align-items: baseline;
`;

const AccountActions = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.space.xs}px;
`;

export const DriveAccountHeader = ({
    accounts,
    activeAccount,
    onLogout,
    onLogin,
    onSetDefaultAccount,
    handlers,
    isLogged,
    onLoginError,
    service,
    description,
    onError,
}: DriveAccountHeaderProps) => {
    const intl = useIntl();
    const theme = useTheme();
    const abortController = useAbortController();
    const [loading, setLoading] = useState(false);

    const deleteAccount = useCallback(() => {
        if (!activeAccount) return;
        setLoading(true);
        handlers.drive
            .logout({ id: activeAccount.id, service })
            .then(() => {
                setLoading(false);
                onLogout();
            })
            .catch((err) => {
                if (onError && !abortController.signal.aborted) {
                    onError(err);
                    setLoading(false);
                }
            });
    }, [abortController.signal.aborted, activeAccount, handlers.drive, onError, onLogout, service]);

    const openLoginPopup = useCallback(() => {
        const popup = window.open(
            '/?action=drive_connect&name=' + service,
            '',
            'menubar=no,status=no,scrollbars=no,menubar=no,width=500,height=700',
        );
        const interval = setInterval(() => {
            const id = popup?.document.body.querySelector('div')?.getAttribute('data-drive-account-id');
            const isPersonal =
                popup?.document.body.querySelector('div')?.getAttribute('data-drive-is-personal') === 'true';
            if (popup && id) {
                if (isPersonal) {
                    onLoginError(intl.formatMessage({ id: 'DRIVE_MS_Error_Personal_Account' }));
                } else {
                    handlers.drive.getAccount({ service, id }, { signal: abortController.signal }).then((res) => {
                        onLogin(res.result.account);
                    });
                }
                clearInterval(interval);
                popup.close();
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [abortController.signal, handlers.drive, intl, onLogin, onLoginError, service]);

    const setDefaultAccount = useCallback(
        (account: DriveAccount) => {
            setLoading(true);
            handlers.drive
                .setDefaultAccount(
                    {
                        account: account.id,
                        service,
                    },
                    { signal: abortController.signal },
                )
                .then(() => {
                    setLoading(false);
                    onSetDefaultAccount(account);
                })
                .catch((err) => {
                    if (onError && !abortController.signal.aborted) {
                        onError(err);
                        setLoading(false);
                    }
                });
        },
        [abortController.signal, handlers.drive, onError, onSetDefaultAccount, service],
    );

    return (
        <Container>
            <JRCDisplayCard>
                <InnerContainer>
                    {activeAccount && accounts ? (
                        <>
                            <div>
                                <Account>
                                    <JRCTypography>{activeAccount.title}</JRCTypography>
                                    {activeAccount.default && (
                                        <JRCTypography size={'s'} color={'grey4'}>
                                            ({intl.formatMessage({ id: 'DRIVE_MS_Default' })})
                                        </JRCTypography>
                                    )}
                                </Account>
                                <JRCTypography size={'s'}>{activeAccount.mail}</JRCTypography>
                            </div>
                            {loading ? (
                                <div>
                                    <JRCLoader />
                                </div>
                            ) : (
                                <AccountActions>
                                    {!isLogged && (
                                        <JRCButton
                                            label={intl.formatMessage({ id: 'GLOBAL_Login' })}
                                            onClick={openLoginPopup}
                                        />
                                    )}
                                    <JRCButtonDropdown
                                        variant={'outlined'}
                                        color={'cancel'}
                                        options={[
                                            ...(!activeAccount.default
                                                ? [
                                                      {
                                                          icon: { name: 'icon-check', color: theme.color.grey4 },
                                                          label: intl.formatMessage({
                                                              id: 'DRIVE_MS_Set_Default_Account',
                                                          }),
                                                          onClick: () => setDefaultAccount(activeAccount),
                                                      },
                                                  ]
                                                : []),
                                            ...accounts
                                                .filter((a) => a.id !== activeAccount.id)
                                                .map((account) => ({
                                                    icon: { name: 'icon-arrows-rotate', color: theme.color.grey4 },
                                                    label: account.mail,
                                                    onClick: () => onLogin(account),
                                                })),
                                            {
                                                icon: { name: 'icon-times', color: theme.color.grey4 },
                                                label: intl.formatMessage({ id: 'DRIVE_MS_Logout' }),
                                                onClick: deleteAccount,
                                            },
                                            {
                                                icon: { name: 'icon-plus', color: theme.color.grey4 },
                                                label: intl.formatMessage({ id: 'DRIVE_MS_Other_Account' }),
                                                onClick: openLoginPopup,
                                            },
                                        ]}
                                        label={intl.formatMessage({ id: 'DRIVE_MS_Accounts' })}
                                        icon={'icon-gear'}
                                        small
                                        smallOpenDirection={'left'}
                                    />
                                </AccountActions>
                            )}
                        </>
                    ) : (
                        <>
                            <div>
                                <JRCTypography>{intl.formatMessage({ id: 'DRIVE_MS_Need_Login' })}</JRCTypography>
                                {description && (
                                    <JRCTypography color={'grey4'} size={'s'}>
                                        {description}
                                    </JRCTypography>
                                )}
                            </div>
                            <JRCButton label={intl.formatMessage({ id: 'GLOBAL_Login' })} onClick={openLoginPopup} />
                        </>
                    )}
                </InnerContainer>
            </JRCDisplayCard>
        </Container>
    );
};
